// QueriesAdminTab — one of 3 tabs in KnowledgeAdmin.jsx (added 3 Aug 2026
// for the retrieval-architecture rebuild, see ai-chat-tracker.md).
//
// Admin-authored bank of reference SQL queries the agent's
// `search_sample_queries` tool searches by description/tags (not by SQL
// text — see query_kb.py). Deliberately admin-only end to end (no
// propose/approve flow like the Lessons tab) — a bad auto-generated
// "reference query" is a worse failure mode than a bad lesson, per the
// tracker's locked decision.

import { useState, useEffect, useMemo } from "react";
import {
  SpaceBetween, Container, Header, Box, Button, Table, Spinner, Alert,
  Modal, FormField, Input, Textarea, TextFilter,
} from "@cloudscape-design/components";
import { aiChatApi } from "./aiChatApi.js";

export default function QueriesAdminTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  const filteredItems = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      if (item.title?.toLowerCase().includes(q)) return true;
      if (item.description?.toLowerCase().includes(q)) return true;
      if ((item.tags || []).some((t) => t.toLowerCase().includes(q))) return true;
      if (item.created_by?.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [items, searchText]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await aiChatApi.listQueries();
      setItems(data.items || []);
    } catch (err) {
      setError(err.message || "Failed to load sample queries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (queryId) => {
    const confirmed = window.confirm("Delete this reference query? This cannot be undone.");
    if (!confirmed) return;
    setDeletingId(queryId);
    try {
      await aiChatApi.deleteQuery(queryId);
      setItems((prev) => prev.filter((i) => i.query_id !== queryId));
    } catch (err) {
      setError(err.message || "Failed to delete.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleSaveEdit = async ({ title, description, sql, tags }) => {
    const updated = await aiChatApi.updateQuery(editingItem.query_id, { title, description, sql, tags });
    setItems((prev) => prev.map((i) => (i.query_id === editingItem.query_id ? updated : i)));
    setEditingItem(null);
  };

  const handleAdd = async ({ title, description, sql, tags }) => {
    const created = await aiChatApi.createQuery({ title, description, sql, tags });
    setItems((prev) => [created, ...prev]);
    setAddModalOpen(false);
  };

  return (
    <SpaceBetween size="l">
      {error && <Alert type="error" dismissible onDismiss={() => setError("")}>{error}</Alert>}

      <Container
        header={
          <Header
            variant="h2"
            description="Admin-validated reference queries the agent searches via search_sample_queries — adapting a known-good query instead of writing from scratch."
            counter={`(${filteredItems.length}${filteredItems.length !== items.length ? ` of ${items.length}` : ""})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="refresh" onClick={load} loading={loading}>Refresh</Button>
                {isAdmin && (
                  <Button variant="primary" iconName="add-plus" onClick={() => setAddModalOpen(true)}>
                    Add query
                  </Button>
                )}
              </SpaceBetween>
            }
          >
            📝 Reference queries
          </Header>
        }
      >
        {loading ? (
          <Box textAlign="center" padding="l"><Spinner size="large" /></Box>
        ) : (
          <SpaceBetween size="m">
            <TextFilter
              filteringText={searchText}
              onChange={({ detail }) => setSearchText(detail.filteringText)}
              filteringPlaceholder="Search by title, description, or tags..."
              countText={searchText ? `${filteredItems.length} of ${items.length}` : undefined}
            />
            <Table
              variant="embedded"
              resizableColumns
              items={filteredItems}
            columnDefinitions={[
              {
                id: "title", header: "Title", width: 260, minWidth: 180,
                cell: (item) => (
                  <div className="ai-chat-query-title-cell">
                    <span className="ai-chat-content-cell">{item.title}</span>
                    {isAdmin && (
                      <span className="ai-chat-query-title-actions">
                        <Button variant="icon" iconName="edit" ariaLabel="Edit" onClick={() => setEditingItem(item)} />
                        <Button
                          variant="icon" iconName="remove" ariaLabel="Delete"
                          loading={deletingId === item.query_id}
                          onClick={() => handleDelete(item.query_id)}
                        />
                      </span>
                    )}
                  </div>
                ),
              },
              {
                id: "description", header: "Description", minWidth: 250,
                cell: (item) => <span className="ai-chat-content-cell">{item.description}</span>,
              },
              {
                id: "tags", header: "Tags", width: 200, minWidth: 140,
                cell: (item) => (item.tags || []).length
                  ? <span className="ai-chat-chip-list">{(item.tags || []).map((t, i) => <span key={i} className="ai-chat-chip">{t}</span>)}</span>
                  : "—",
              },
              {
                id: "created_by", header: "Added by", width: 150, minWidth: 90,
                cell: (item) => item.created_by || "-",
              },
            ]}
            empty={
              <Box textAlign="center" color="inherit" padding="l">
                <b>No reference queries yet</b>
                <Box color="text-body-secondary">Add a validated query the agent can adapt for common questions.</Box>
              </Box>
            }
          />
          </SpaceBetween>
        )}
      </Container>

      {editingItem && (
        <QueryEditModal item={editingItem} onDismiss={() => setEditingItem(null)} onSave={handleSaveEdit} />
      )}
      {addModalOpen && (
        <QueryEditModal item={null} onDismiss={() => setAddModalOpen(false)} onSave={handleAdd} />
      )}
    </SpaceBetween>
  );
}

function QueryEditModal({ item, onDismiss, onSave }) {
  const isNew = !item;
  const [title, setTitle] = useState(item?.title || "");
  const [description, setDescription] = useState(item?.description || "");
  const [sql, setSql] = useState(item?.sql || "");
  const [tagsText, setTagsText] = useState((item?.tags || []).join(", "));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    if (!title.trim() || !sql.trim()) {
      setError("Title and SQL are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const tags = tagsText.split(",").map((t) => t.trim()).filter(Boolean);
      await onSave({ title: title.trim(), description: description.trim(), sql: sql.trim(), tags });
    } catch (err) {
      setError(err.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      size="large"
      header={isNew ? "Add reference query" : "Edit reference query"}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onDismiss}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleSave}>Save</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && <Alert type="error">{error}</Alert>}
        <FormField label="Title">
          <Input value={title} onChange={({ detail }) => setTitle(detail.value)} placeholder="e.g. Weekly TPV by channel" />
        </FormField>
        <FormField label="Description" description="What question does this query answer? This is what the agent's search matches against — not the SQL text.">
          <Textarea value={description} onChange={({ detail }) => setDescription(detail.value)} rows={3} />
        </FormField>
        <FormField label="SQL">
          <Textarea value={sql} onChange={({ detail }) => setSql(detail.value)} rows={10} />
        </FormField>
        <FormField label="Tags" description="Comma-separated, e.g. tpv, channel, weekly">
          <Input value={tagsText} onChange={({ detail }) => setTagsText(detail.value)} />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
